import { useEffect, useMemo, useState } from 'react';

import { Badge, IntelSignalCard } from 'design-system';

import { parseSymbolKeys } from '@/entities/symbol';
import SymbolsPreview from '@/features/_shared/SymbolsPreview';
import { mapFeedInfoToArticle } from '@/features/markets/components/MonthlyRecapArticle/mapFeedInfoToArticle';
import { getTimeSince } from '@/shared/lib/date/getTimeSince';

import type { FeedInfo } from '../types';
import { FeedCta } from './FeedCta';
import { FeedIcon } from './FeedIcon';

export const FeedRow = (feedInfo: FeedInfo) => {
  const {
    values: { Title, Content, CTAType, CTAValue, CTALabel },
    symbol,
    transactionTime
  } = feedInfo;
  const [time, setTime] = useState(getTimeSince(transactionTime));
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(getTimeSince(transactionTime));
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [transactionTime]);

  const symbols = useMemo(
    () => (CTAType === 'Symbol' ? parseSymbolKeys(CTAValue) : undefined),
    [CTAType, CTAValue]
  );
  const article = useMemo(
    () => (CTAType === 'Article' ? mapFeedInfoToArticle(feedInfo) : undefined),
    [CTAType, feedInfo]
  );
  const hasMissingSymbolDetail = CTAType === 'Symbol' && !symbols?.length;
  const action =
    CTAType || hasMissingSymbolDetail ? (
      <div className='flex flex-wrap items-center gap-2'>
        {CTAType ? (
          <FeedCta
            article={article}
            CTALabel={CTALabel}
            CTAType={CTAType}
            CTAValue={CTAValue}
            open={open}
            setOpen={setOpen}
          />
        ) : null}
        {hasMissingSymbolDetail ? <Badge variant='muted'>Symbol details unavailable</Badge> : null}
      </div>
    ) : undefined;

  return (
    <div className='space-y-2'>
      <IntelSignalCard
        action={action}
        timestamp={time}
        title={
          <span className='inline-flex items-center gap-2'>
            <FeedIcon Type={symbol.Type} />
            <span>{Title}</span>
          </span>
        }
      >
        {Content.replaceAll(/(^")|("$)/g, '')}
      </IntelSignalCard>
      {open && symbols?.length ? (
        <SymbolsPreview
          items={symbols.map(symbolItem => ({ symbol: symbolItem, date: transactionTime }))}
        />
      ) : null}
    </div>
  );
};
